/** The API's only legal `onAccent` foregrounds; compare against these rather than restating the hex. */
export const ON_ACCENT_BLACK = '#000000';
export const ON_ACCENT_WHITE = '#FFFFFF';

export interface CompanyTheme {
  schemaVersion: 1;
  companyId: string;
  revision: string;
  primaryColor: string;
  secondaryColor: string;
  onAccent: '#000000' | '#FFFFFF';
}

export type CompanyPaletteValidation =
  | {
      valid: true;
      primaryColor: string;
      secondaryColor: string;
      onAccent: '#000000' | '#FFFFFF';
      minimumContrast: number;
      hasContrastWarning: boolean;
    }
  | { valid: false; error: 'invalid_color' };

function normalizeColor(input: unknown) {
  if (typeof input !== 'string') return undefined;
  const color = input.trim().toUpperCase();
  if (/^#[0-9A-F]{6}$/.test(color)) return color;
  if (/^#[0-9A-F]{3}$/.test(color)) {
    return `#${color
      .slice(1)
      .split('')
      .map((channel) => channel + channel)
      .join('')}`;
  }
  return undefined;
}

function luminance(color: string, blackOverlay = 0) {
  const channels = [1, 3, 5].map((offset) => {
    const value = (parseInt(color.slice(offset, offset + 2), 16) / 255) * (1 - blackOverlay);
    return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  });
  const [red = 0, green = 0, blue = 0] = channels;
  return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
}

/**
 * Normalizes opaque hex accents and chooses one black/white label for both modes.
 * The selected foreground maximizes its minimum contrast across both fills and their
 * shared pressed black overlays (20% on primary, 40% on secondary). Palettes
 * below 4.5:1 stay valid and report a warning so saved themes round-trip.
 *
 * @param primary - Light-mode accent, accepting trimmed #RGB or #RRGGBB.
 * @param secondary - Dark-mode accent, also used for light-mode hover.
 * @returns A normalized palette with its worst contrast, or a validation code.
 */
export function validateCompanyPalette(
  primary: unknown,
  secondary: unknown
): CompanyPaletteValidation {
  const primaryColor = normalizeColor(primary);
  const secondaryColor = normalizeColor(secondary);
  if (!primaryColor || !secondaryColor) return { valid: false, error: 'invalid_color' };

  const backgrounds = [
    luminance(primaryColor),
    luminance(secondaryColor),
    luminance(primaryColor, 0.2),
    luminance(secondaryColor, 0.4),
  ];
  const blackContrast = Math.min(...backgrounds.map((background) => (background + 0.05) / 0.05));
  const whiteContrast = Math.min(...backgrounds.map((background) => 1.05 / (background + 0.05)));
  const minimumContrast = Math.max(blackContrast, whiteContrast);

  return {
    valid: true,
    primaryColor,
    secondaryColor,
    onAccent: blackContrast >= whiteContrast ? '#000000' : '#FFFFFF',
    minimumContrast,
    hasContrastWarning: minimumContrast < 4.5,
  };
}

/**
 * Accepts only the versioned public theme projection and its validated foreground.
 * Missing, malformed, unsupported or mismatched company data means TIO fallback.
 *
 * @param input - Untrusted public-theme payload from preregistration or company reads.
 * @param expectedCompanyId - When supplied, rejects a theme for another company.
 * @returns A normalized projection, or undefined without mutating the payload.
 */
export function normalizeCompanyTheme(
  input: unknown,
  expectedCompanyId?: string
): CompanyTheme | undefined {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return undefined;
  const candidate = input as Record<string, unknown>;
  if (
    candidate['schemaVersion'] !== 1 ||
    typeof candidate['companyId'] !== 'string' ||
    !candidate['companyId'].trim() ||
    (expectedCompanyId !== undefined && candidate['companyId'] !== expectedCompanyId) ||
    typeof candidate['revision'] !== 'string' ||
    !candidate['revision'].trim()
  )
    return undefined;

  const palette = validateCompanyPalette(candidate['primaryColor'], candidate['secondaryColor']);
  if (!palette.valid || normalizeColor(candidate['onAccent']) !== palette.onAccent)
    return undefined;

  return {
    schemaVersion: 1,
    companyId: candidate['companyId'],
    revision: candidate['revision'],
    primaryColor: palette.primaryColor,
    secondaryColor: palette.secondaryColor,
    onAccent: palette.onAccent,
  };
}
